import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Text } from '@quintoandar-tokko/jopijs';
import BaseModal from '../../BaseModal';
import {
  clearPerformanceModalFilters,
  setPerformanceModals,
} from '@/store/slices/components/dashboard/DashboardSlice';
import buildQuery from '@/components/Dashboard/utils/buildQuery';
import { getPerformanceConversionRateTable } from '@/store/slices/components/dashboard/service';
import Trans from '@/components/shared/Trans';
import translations from '@/components/Dashboard/Dashboard.trans.json';
import DownloadButton from '@/components/Dashboard/DashboardTables/components/DownloadButton';
import parentStyles from '../../performanceModals.module.scss';
import specificStyles from './conversionRateModal.module.scss';
import buildTableQuery from '@/components/Dashboard/utils/buildTableQuery';
import useAsyncTableExcelDownload from '@/components/Dashboard/customHooks/useAsyncTableExcelDownload';
import { TABLES } from '@/components/Dashboard/constants';
import TableSearchEmptyState from '@/components/Dashboard/DashboardTables/TableSearchEmptyState';
import SearchInput from '../../SearchInput';
import ConversionRateTable from '@/components/Dashboard/DashboardTables/ConversionRateTable';
import Checkbox from '@/components/checkbox/Checkbox';
import TableDropdown from '@/components/Dashboard/DashboardTables/components/TableDropdown';
import { conversionRateStatusOptions } from '../utils';
import UseShowGroupByBranch from '@/components/Dashboard/utils/UseShowGroupByBranch';
import { AGENT } from '@/components/FilterBar/constants/filtersConstants';
import useTrackPerformanceTable from '@/components/Dashboard/customHooks/useTrackDashboard';
import { TRACK_ELEMENT } from '@/components/Dashboard/constants/track';

export default function ConversionRateModal() {
  const dispatch = useDispatch();
  const { selectedIds, selectAgents, selectBranches } = useSelector(
    (state) => state.FilterBarSlice.filters
  );
  const { conversionRate } = useSelector(
    (state) => state.DashboardSlice.performanceModals
  );
  const {
    conversion_rate,
    list,
    loading,
    sortBy,
    sortDirection,
    searchQuery,
    searchTypeFlag,
    searchStatus,
    groupByBranch,
  } = conversionRate;

  const showGroupByBranch = UseShowGroupByBranch();
  const track = useTrackPerformanceTable();
  const { handleDownloadExcel: downloadExcel, isDownloading } =
    useAsyncTableExcelDownload(TABLES.CONVERSION_RATE);

  useEffect(() => {
    const fetchData = () => {
      const query = buildQuery(selectedIds);
      const tableQuery = buildTableQuery(conversionRate);

      dispatch(getPerformanceConversionRateTable({ query, tableQuery }));
    };

    fetchData();
  }, [
    sortBy,
    sortDirection,
    searchQuery,
    searchTypeFlag,
    searchStatus,
    groupByBranch,
  ]);

  const handleDownloadExcel = () => {
    const query = buildQuery(selectedIds);
    const tableQuery = buildTableQuery(conversionRate);
    downloadExcel(query, tableQuery);
    track.conversionRate(TRACK_ELEMENT.DOWNLOAD_BUTTON);
  };

  const handleClearFilters = () => {
    const modal = 'conversionRate';
    const filters = ['searchQuery', 'searchStatus', 'groupByBranch'];
    dispatch(clearPerformanceModalFilters({ modal, filters }));
  };

  const selectedStatusOption =
    conversionRateStatusOptions.find(
      (option) => option.value === searchStatus
    ) ?? conversionRateStatusOptions[1];

  const uniqueAgent =
    selectedIds.filter((x) => x.filter_type === AGENT).length === 1;

  const handleBranchGroupCheck = () => {
    dispatch(
      setPerformanceModals({
        key: 'conversionRate',
        subkey: 'groupByBranch',
        value: !groupByBranch,
      })
    );
    track.conversionRate(TRACK_ELEMENT.BRANCH_GROUP_CHECKBOX);
  };

  const handleAgentBranchChange = (option) => {
    dispatch(
      setPerformanceModals({
        key: 'conversionRate',
        subkey: 'searchStatus',
        value: option.value,
      })
    );
    track.conversionRate(TRACK_ELEMENT.DROPDOWN_SHOW_ALL);
  };

  return (
    <BaseModal
      id="conversion-rate-modal"
      title={`${Trans(translations, 'ConversionRate')}: ${
        conversion_rate?.rate ?? '0'
      }`}
      className={specificStyles.conversionRateModal}
      handleClose={() =>
        dispatch(
          setPerformanceModals({
            key: 'conversionRate',
            subkey: 'isOpen',
            value: false,
          })
        )
      }
    >
      <Box className={parentStyles.modalControls}>
        <SearchInput
          modal="conversionRate"
          maxWidth={selectBranches.length > 1 ? 520 : '100%'}
        />

        {showGroupByBranch && (
          <Box
            className={parentStyles.checkboxWrapper}
            onClick={handleBranchGroupCheck}
          >
            <Checkbox checked={groupByBranch} />
            <Box as="span" className={parentStyles.checkboxText}>
              {Trans(translations, 'GroupByBranch')}
            </Box>
          </Box>
        )}

        {(selectAgents.length > 1 || selectBranches.length > 1) &&
          !uniqueAgent && (
            <Box className={parentStyles.dropdownWrapper}>
              <Text variant={parentStyles.dropdownWrapperLabel}>
                {Trans(translations, 'NewContactsTable.Show')}
              </Text>
              <TableDropdown
                selectedOption={selectedStatusOption}
                options={conversionRateStatusOptions}
                onSelect={(option) => handleAgentBranchChange(option)}
              />
            </Box>
          )}

        <DownloadButton
          onClick={handleDownloadExcel}
          disabled={isDownloading}
        />
      </Box>
      {searchQuery && list?.length === 0 && !loading ? (
        <TableSearchEmptyState onClearFilters={handleClearFilters} />
      ) : (
        <BaseModal.TableContainer>
          <ConversionRateTable />
        </BaseModal.TableContainer>
      )}
    </BaseModal>
  );
}
